import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { 
  Sparkles, 
  TrendingUp, 
  Shield, 
  CheckCircle2, 
  User, 
  Compass, 
  Brain, 
  BookOpen, 
  Layers, 
  Award, 
  ChevronRight, 
  Briefcase, 
  GraduationCap, 
  Home, 
  Clock, 
  Activity, 
  Sliders, 
  Users, 
  Heart, 
  Check, 
  HelpCircle,
  FileCheck,
  Zap,
  Globe,
  Sun,
  Flame,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Lightbulb,
  MessageSquare,
  Sparkle
} from 'lucide-react';
import { calculateShadbala, getBirthUtcDate } from '../utils/vedicEngine';
import { generateIntegratedProfile } from '../utils/integratedProfileGenerator';
import { AstrologerCoreView } from './AstrologerCoreView';
import { DashaEvolutionTimelineView } from './DashaEvolutionTimelineView';
import { CareerBusinessVsJobModule } from './CareerBusinessVsJobModule';
import { 
  identifyChartArchetype, 
  generateHumanizedProfile 
} from '../utils/humanizedToneEngine';

interface AnalyticsReportViewProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const AnalyticsReportView: React.FC<AnalyticsReportViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  
  // Primary Navigation: 1. Analytics / Integrated Life Profile, 2. Astrologer Core View, 3. Dasha Evolution, 4. On-Demand Life Themes, 5. Timing Windows, 6. Remedial Wisdom
  const [activeViewMode, setActiveViewMode] = useState<'integrated-profile' | 'core-view' | 'dasha-evolution' | 'detailed-personality' | 'theme-deep-dive' | 'timing-windows' | 'remedial-harmony'>('integrated-profile');

  // Humanized Voice Language Sub-Toggle
  const [conversationalLang, setConversationalLang] = useState<'hinglish' | 'hi' | 'en'>('hinglish');
  
  // Dynamic Current Date (NEVER hardcoded)
  const now = new Date();
  const currentMonthName = now.toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-US', { month: 'long' });
  const currentYear = now.getFullYear();
  const currentMonthYearStr = `${currentMonthName} ${currentYear}`;

  // Age auto-detection for Child Life-Stage Gate
  const detectAgeFromBirthDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const bDate = new Date(dateStr);
    if (isNaN(bDate.getTime())) return null;
    let age = now.getFullYear() - bDate.getFullYear();
    const m = now.getMonth() - bDate.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < bDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  };

  const detectedAge = detectAgeFromBirthDate(kundali.birthDetails?.date);

  // Generate the Deep Multi-Factor Synthesized Integrated Profile in Natural Everyday Language
  const integratedProfile = generateIntegratedProfile(kundali, lang);
  const humanizedNarrative = generateHumanizedProfile(kundali);

  const getProfileIcon = (iconName: string) => {
    switch (iconName) {
      case 'Compass': return <Compass className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Brain': return <Brain className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Heart': return <Heart className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Users': return <Users className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Flame': return <Flame className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Briefcase': return <Briefcase className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Award': return <Award className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'AlertTriangle': return <AlertTriangle className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Zap': return <Zap className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'Clock': return <Clock className="w-5 h-5 text-astro-accent shrink-0" />;
      case 'CheckCircle2': return <CheckCircle2 className="w-5 h-5 text-astro-accent shrink-0" />;
      default: return <Sparkles className="w-5 h-5 text-astro-accent shrink-0" />;
    }
  };

  // On-Demand Life Theme Deep-Dive — Strict Priority Ordering:
  // 1. Child Development, Education & Future -> 2. Career -> 3. Marriage -> 4. Wealth -> 5. Health -> 6. Property -> 7. Business -> 8. Travel -> 9. Spirituality -> 10. Public Life -> 11. Progeny -> 12. Higher Ed
  const [selectedDeepDiveTheme, setSelectedDeepDiveTheme] = useState<
    'child_development' | 'career' | 'marriage' | 'wealth' | 'health' | 'property' | 'business' | 'travel' | 'spirituality' | 'politics' | 'children' | 'education'
  >('child_development');

  // Contextual inputs (Desh-Kaal-Patra) for Life Theme Deep-Dives
  const [careerStage, setCareerStage] = useState<'student' | 'job_seeker' | 'employed' | 'business' | 'executive'>('employed');
  const [careerHorizon, setCareerHorizon] = useState<'1yr' | '3yr' | '5yr'>('3yr');
  const [maritalStatus, setMaritalStatus] = useState<'unmarried' | 'married' | 'separated' | 'future_query'>('unmarried');
  const [spouseKundaliAvailable, setSpouseKundaliAvailable] = useState<boolean>(false);
  const [educationStage, setEducationStage] = useState<'school' | 'undergrad' | 'postgrad' | 'research_competitive'>('postgrad');
  const [ageGroup, setAgeGroup] = useState<'youth' | 'adult' | 'midlife' | 'senior'>('adult');

  // v5.12 Child Development, Education & Career Context State
  const [childStage, setChildStage] = useState<
    'early_childhood' | 'primary_school' | 'middle_secondary' | 'higher_secondary' | 'undergrad_college' | 'postgrad_early_career'
  >(() => {
    if (detectedAge !== null) {
      if (detectedAge <= 5) return 'early_childhood';
      if (detectedAge <= 10) return 'primary_school';
      if (detectedAge <= 15) return 'middle_secondary';
      if (detectedAge <= 18) return 'higher_secondary';
      if (detectedAge <= 23) return 'undergrad_college';
      return 'postgrad_early_career';
    }
    return 'primary_school';
  });

  const [childSubTheme, setChildSubTheme] = useState<
    'integrated' | 'temperament' | 'learning_style' | 'school_education' | 'concentration' | 'competitive_exams' | 'higher_education' | 'foreign_education' | 'career_direction' | 'leadership' | 'educational_timing' | 'timing_windows' | 'supportive_guidance' | 'gemstone_assessment'
  >('integrated');

  const [childChartContext, setChildChartContext] = useState<'child_own_chart' | 'parent_chart_inquiry'>('child_own_chart');
  const [childCareerHorizon, setChildCareerHorizon] = useState<'1yr' | '3yr' | '5yr'>('3yr');

  // Age & Stage Gate: check if stage is premature for higher education or career forecasts
  const isPrematureStage = childStage === 'early_childhood' || childStage === 'primary_school';
  const isHigherStageQuery = ['higher_education', 'foreign_education', 'career_direction', 'competitive_exams'].includes(childSubTheme);

  // Advanced Jyotishi Technical Evidence Drawer
  const [showAdvancedEvidence, setShowAdvancedEvidence] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Deterministic chart calculations
  const shadbalaResult = kundali.shadbala || calculateShadbala(kundali.charts.D1.planets, kundali.houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const lagnaHouse = kundali.houses.find(h => h.houseNumber === 1);
  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const sunPlanet = kundali.planets.find(p => p.name === 'Sun');
  const marsPlanet = kundali.planets.find(p => p.name === 'Mars');
  const mercuryPlanet = kundali.planets.find(p => p.name === 'Mercury');
  const jupiterPlanet = kundali.planets.find(p => p.name === 'Jupiter');
  const venusPlanet = kundali.planets.find(p => p.name === 'Venus');
  const saturnPlanet = kundali.planets.find(p => p.name === 'Saturn');
  const rahuPlanet = kundali.planets.find(p => p.name === 'Rahu');
  const ketuPlanet = kundali.planets.find(p => p.name === 'Ketu');

  // Key Houses
  const secondHouse = kundali.houses.find(h => h.houseNumber === 2);
  const fourthHouse = kundali.houses.find(h => h.houseNumber === 4);
  const fifthHouse = kundali.houses.find(h => h.houseNumber === 5);
  const sixthHouse = kundali.houses.find(h => h.houseNumber === 6);
  const seventhHouse = kundali.houses.find(h => h.houseNumber === 7);
  const ninthHouse = kundali.houses.find(h => h.houseNumber === 9);
  const tenthHouse = kundali.houses.find(h => h.houseNumber === 10);
  const eleventhHouse = kundali.houses.find(h => h.houseNumber === 11);
  const twelfthHouse = kundali.houses.find(h => h.houseNumber === 12);

  // Active Vimshottari Dasha
  const currentDasha = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  const activeAntardasha = currentDasha?.antardashas?.find(ad => {
    const start = new Date(ad.startDate).getTime();
    const end = new Date(ad.endDate).getTime();
    const current = now.getTime();
    return current >= start && current <= end;
  }) || currentDasha?.antardashas?.[0];

  // Strongest and Weakest planets
  const sortedShadbala = [...shadbalaResult.planets].sort((a, b) => b.totalRupas - a.totalRupas);
  const strongestPlanet = sortedShadbala[0];
  const weakestPlanet = sortedShadbala[sortedShadbala.length - 1];

  // SAV Bindus
  const savBindus = kundali.ashtakvarga.sarvashtakvarga;
  const lagnaSAV = savBindus[0] || 28;
  const secondSAV = savBindus[1] || 28;
  const fourthSAV = savBindus[3] || 28;
  const fifthSAV = savBindus[4] || 28;
  const seventhSAV = savBindus[6] || 28;
  const tenthSAV = savBindus[9] || 28;
  const eleventhSAV = savBindus[10] || 28;

  // D9 Navamsha & Vargas
  const d9Moon = kundali.charts.D9?.planets.find(p => p.name === 'Moon');
  const d9LagnaLord = kundali.charts.D9?.planets.find(p => p.name === (lagnaHouse?.signLord as any));
  const d10TenthLord = kundali.charts.D10?.planets.find(p => p.name === (tenthHouse?.signLord as any));
  const d10Mars = kundali.charts.D10?.planets.find(p => p.name === 'Mars');
  const d10Sun = kundali.charts.D10?.planets.find(p => p.name === 'Sun');
  const d7FifthLord = kundali.charts.D7?.planets.find(p => p.name === (fifthHouse?.signLord as any));
  const d24Mercury = kundali.charts.D24?.planets.find(p => p.name === 'Mercury');
  const d24Jupiter = kundali.charts.D24?.planets.find(p => p.name === 'Jupiter');
  const d24FifthLord = kundali.charts.D24?.planets.find(p => p.name === (fifthHouse?.signLord as any));
  const d24NinthLord = kundali.charts.D24?.planets.find(p => p.name === (ninthHouse?.signLord as any));

  const handleSaveConsultation = () => {
    setSaveStatus(true);
    setTimeout(() => setSaveStatus(false), 3000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-fade-in text-astro-text">
      
      {/* Top Banner: Professional Integrated Astrological Consultation */}
      <div className="bg-gradient-to-r from-astro-primary via-[#113837] to-[#0D2D2C] text-white p-6 sm:p-8 rounded-3xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 border border-astro-accent/30">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-astro-accent mb-1.5">
            <Sparkles className="w-4 h-4" />
            <span>{isHi ? 'वेदांग ज्योतिषम् — समग्र शास्त्रीय परामर्श' : 'VEDANGJYOTISHAM — Comprehensive Astrological Consultation'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold font-serif flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-astro-accent" />
            {isHi ? 'एकीकृत व्यक्तित्व एवं जीवन निर्णय' : 'INTEGRATED PERSONALITY & LIFE PROFILE'}
          </h2>
          <p className="text-xs sm:text-sm text-astro-border mt-1.5 max-w-2xl leading-relaxed">
            {isHi 
              ? 'सम्पूर्ण जन्म कुंडली एवं इसके सहायक वर्गों पर आधारित विस्तृत शास्त्रीय परामर्श।' 
              : 'A detailed reading based on the complete horoscope and its supporting charts.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <button
            onClick={() => setShowAdvancedEvidence(!showAdvancedEvidence)}
            className="bg-[#1D5E5C] hover:bg-[#236F6D] text-white font-medium px-4 py-2.5 rounded-xl border border-astro-accent/40 text-xs flex items-center gap-2 transition-all cursor-pointer shadow-xs"
            title="Inspect underlying astrological factors"
          >
            <Sliders className="w-4 h-4 text-astro-accent" />
            <span>{showAdvancedEvidence ? (isHi ? 'तकनीकी साक्ष्य छुपाएं' : 'Hide Technical Evidence') : (isHi ? 'उन्नत ज्योतिषी साक्ष्य' : 'Advanced Evidence Explorer')}</span>
          </button>
          
          {saveStatus && (
            <span className="text-xs bg-emerald-600 text-white font-bold px-3.5 py-2.5 rounded-xl flex items-center gap-1.5 shadow-md animate-bounce">
              <CheckCircle2 className="w-4 h-4" /> {isHi ? 'सहेजा गया!' : 'Saved!'}
            </span>
          )}

          <button
            onClick={handleSaveConsultation}
            className="bg-astro-accent hover:bg-[#9E824C] text-astro-text font-bold px-5 py-2.5 rounded-xl shadow-lg text-xs sm:text-sm flex items-center gap-2 transition-all cursor-pointer transform hover:scale-105"
          >
            <TrendingUp className="w-4 h-4" />
            <span>{isHi ? 'परामर्श सहेजें' : 'Save Consultation'}</span>
          </button>
        </div>
      </div>

      {/* Advanced Jyotishi Evidence Explorer (Collapsible Technical Matrix) */}
      {showAdvancedEvidence && (
        <div className="bg-[#FAF6ED] p-6 sm:p-7 rounded-3xl border border-astro-accent/50 shadow-md space-y-4 animate-fade-in">
          <div className="flex items-center justify-between border-b border-astro-border pb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-astro-primary" />
              <h4 className="font-serif font-bold text-sm text-astro-primary">
                {isHi ? 'उन्नत ज्योतिषी साक्ष्य अन्वेषक' : 'Advanced Astrological Evidence Explorer'}
              </h4>
            </div>
            <span className="text-[11px] bg-astro-border px-2.5 py-1 rounded font-medium text-astro-secondary-text">
              {isHi ? 'शास्त्रीय गणितीय आधार' : 'Deterministic Evidence Model'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            {/* Shadbala Potencies */}
            <div className="bg-astro-bg p-4 rounded-2xl border border-astro-border space-y-2">
              <span className="font-bold text-astro-primary block flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-astro-accent" /> Shadbala Potencies (Rupas)
              </span>
              <div className="space-y-1 text-astro-secondary-text">
                {shadbalaResult.planets.map(p => (
                  <div key={p.planet} className="flex justify-between border-b border-dashed border-astro-border/60 pb-0.5">
                    <span>{p.planet}:</span>
                    <span className="font-semibold text-astro-text">{p.totalRupas} R ({p.strengthCategory})</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ashtakvarga SAV Bindus */}
            <div className="bg-astro-bg p-4 rounded-2xl border border-astro-border space-y-2">
              <span className="font-bold text-astro-primary block flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-astro-accent" /> Sarvashtakvarga (SAV) Bindus
              </span>
              <div className="grid grid-cols-3 gap-1.5 text-center">
                {savBindus.map((b, idx) => (
                  <div key={idx} className={`p-1.5 rounded border text-[11px] ${b >= 28 ? 'bg-emerald-50 text-emerald-900 border-emerald-200 font-bold' : 'bg-[#FAF6ED] text-astro-secondary-text border-astro-border'}`}>
                    H{idx + 1}: {b}
                  </div>
                ))}
              </div>
            </div>

            {/* Varga Placements & Active Dasha */}
            <div className="bg-astro-bg p-4 rounded-2xl border border-astro-border space-y-2">
              <span className="font-bold text-astro-primary block flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-astro-accent" /> Vargas & Active Timing
              </span>
              <div className="space-y-1.5 text-astro-secondary-text">
                <div className="flex justify-between">
                  <span>D9 Navamsha Moon:</span>
                  <span className="font-bold text-astro-text">{d9Moon?.sign || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>D24 Siddhamsa (5th/9th):</span>
                  <span className="font-bold text-astro-text">{d24FifthLord?.sign || 'Supported'} / {d24NinthLord?.sign || 'Strong'}</span>
                </div>
                <div className="flex justify-between">
                  <span>D10 10th Lord in D10:</span>
                  <span className="font-bold text-astro-text">{d10TenthLord?.sign || 'Strong'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Current Mahadasha:</span>
                  <span className="font-bold text-astro-primary">{currentDasha?.planet} ({currentDasha?.startDate} - {currentDasha?.endDate})</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Antardasha:</span>
                  <span className="font-bold text-astro-primary">{activeAntardasha?.planet || 'Active'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Mode Navigation: Clean, elegant bar */}
      <div className="flex bg-astro-card p-1.5 rounded-2xl border border-astro-border overflow-x-auto gap-2 shadow-inner">
        {[
          { id: 'integrated-profile', label: isHi ? 'एकीकृत व्यक्तित्व एवं जीवन परामर्श' : 'Integrated Personality & Life Profile', icon: User },
          { id: 'dasha-evolution', label: isHi ? 'दशा-आधारित जीवन रूपांतरण' : 'Dasha Evolution & Life Phases', icon: Sparkles },
          { id: 'core-view', label: isHi ? 'ज्योतिषी कोर व्यू' : 'Astrologer Core View', icon: Shield },
          { id: 'theme-deep-dive', label: isHi ? 'विशिष्ट जीवन विषय परामर्श' : 'Explore a Specific Life Area', icon: Briefcase },
          { id: 'timing-windows', label: isHi ? 'गतिशील काल निर्णय एवं भविष्य खिड़कियां' : 'Dynamic Future Timing Windows', icon: Clock },
          { id: 'remedial-harmony', label: isHi ? 'शास्त्रीय संतुलन व विवेकपूर्ण मार्गदर्शन' : 'Remedial Wisdom & Harmonization', icon: Shield },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveViewMode(tab.id as any)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all cursor-pointer ${
                activeViewMode === tab.id
                  ? 'bg-astro-primary text-white shadow-md'
                  : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50'
              }`}
            >
              <Icon className="w-4 h-4 text-astro-accent" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* SECTION: DASHA EVOLUTION & LIFE PHASES TIMELINE                          */}
      {/* ========================================================================= */}
      {activeViewMode === 'dasha-evolution' && (
        <div className="animate-fade-in">
          <DashaEvolutionTimelineView
            kundali={kundali}
            lang={lang}
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION: ASTROLOGER CORE VIEW (v5.14 Step 4 Integration)                 */}
      {/* ========================================================================= */}
      {activeViewMode === 'core-view' && (
        <div className="animate-fade-in">
          <AstrologerCoreView
            kundali={kundali}
            lang={lang}
            onOpenPublishedConsultation={() => setActiveViewMode('integrated-profile')}
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 1: DEFAULT COMPREHENSIVE INTEGRATED PERSONALITY & LIFE PROFILE   */}
      {/* ========================================================================= */}
      {activeViewMode === 'integrated-profile' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-9 shadow-md space-y-7">
            
            {/* Direct Consultation Heading with Disclosure */}
            <div className="border-b border-astro-border pb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold text-astro-accent uppercase tracking-wider mb-1">
                    <FileCheck className="w-4 h-4" />
                    <span>{isHi ? 'सम्पूर्ण शास्त्रीय मूल्यांकन' : 'Integrated Personality & Life Profile'}</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary">
                    {isHi ? 'समग्र व्यक्तित्व, स्वभाव एवं जीवन दिशा' : 'Comprehensive Astrological Consultation & Life Ethos'}
                  </h3>
                  <p className="text-xs sm:text-sm text-astro-secondary-text mt-2 leading-relaxed italic bg-[#FAF6ED]/70 p-3 rounded-xl border border-astro-border/80">
                    "{integratedProfile.disclosure}"
                  </p>
                </div>

                <button
                  onClick={() => setActiveViewMode('theme-deep-dive')}
                  className="bg-[#FAF6ED] hover:bg-astro-border text-astro-primary border border-astro-accent/40 font-bold px-4 py-2.5 rounded-xl text-xs flex items-center gap-2 shrink-0 transition-all cursor-pointer shadow-xs"
                >
                  <Briefcase className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'विशिष्ट विषय का गहन परामर्श लें' : 'Explore a Specific Life Area'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Humanized Archetype & Dynamic Tone Indian Voice Section */}
            <div className="bg-gradient-to-br from-[#FAF6ED] via-astro-bg to-[#FAF6ED] p-5 sm:p-7 rounded-2xl border-2 border-astro-accent/50 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3.5">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-astro-accent" />
                    <h4 className="text-base sm:text-lg font-serif font-bold text-astro-primary">
                      {conversationalLang === 'hinglish' ? humanizedNarrative.archetype.titleHinglish : (conversationalLang === 'hi' ? humanizedNarrative.archetype.titleHi : humanizedNarrative.archetype.titleEn)}
                    </h4>
                  </div>
                  <p className="text-xs text-astro-secondary-text mt-1">
                    {conversationalLang === 'hi' ? humanizedNarrative.archetype.toneDescriptionHi : humanizedNarrative.archetype.toneDescriptionEn}
                  </p>
                </div>

                {/* Language Switcher for Voice Engine */}
                <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border self-start sm:self-auto">
                  <button
                    onClick={() => setConversationalLang('hinglish')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      conversationalLang === 'hinglish'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    Hinglish (Natural)
                  </button>
                  <button
                    onClick={() => setConversationalLang('hi')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      conversationalLang === 'hi'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    हिंदी
                  </button>
                  <button
                    onClick={() => setConversationalLang('en')}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      conversationalLang === 'en'
                        ? 'bg-astro-primary text-white shadow-xs'
                        : 'text-astro-secondary-text hover:text-astro-primary'
                    }`}
                  >
                    English
                  </button>
                </div>
              </div>

              {/* Signature Tone Keyword Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-bold text-astro-accent uppercase tracking-wider mr-1">
                  Tone Signatures:
                </span>
                {humanizedNarrative.archetype.signatureKeywords.map((kw, i) => (
                  <span key={i} className="text-xs px-2.5 py-0.5 rounded-full bg-astro-bg border border-astro-accent/40 text-astro-primary font-semibold">
                    #{kw}
                  </span>
                ))}
              </div>

              {/* Core Interpretation Blocks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-1 text-xs">
                {/* 1. Core Nature & Driving Force */}
                <div className="bg-astro-bg p-4 rounded-xl border border-astro-border space-y-1.5">
                  <span className="font-bold text-astro-primary flex items-center gap-1.5 text-xs uppercase tracking-wide">
                    <Compass className="w-4 h-4 text-astro-accent" />
                    {conversationalLang === 'hi' ? 'मूल स्वभाव एवं प्रेरणा' : (conversationalLang === 'hinglish' ? 'Aapka Core Nature & Driving Force' : 'Core Instinct & Driving Force')}
                  </span>
                  <p className="text-[#384342] leading-relaxed text-xs sm:text-sm">
                    {humanizedNarrative.coreNature[conversationalLang]}
                  </p>
                </div>

                {/* 2. Executive Edge & Problem Solving */}
                <div className="bg-astro-bg p-4 rounded-xl border border-astro-border space-y-1.5">
                  <span className="font-bold text-astro-primary flex items-center gap-1.5 text-xs uppercase tracking-wide">
                    <Brain className="w-4 h-4 text-astro-accent" />
                    {conversationalLang === 'hi' ? 'कार्यकुशलता एवं निर्णय-शैली' : (conversationalLang === 'hinglish' ? 'Workplace Superpower & Executive Edge' : 'Executive Edge & Execution')}
                  </span>
                  <p className="text-[#384342] leading-relaxed text-xs sm:text-sm">
                    {humanizedNarrative.executiveEdge[conversationalLang]}
                  </p>
                </div>

                {/* 3. Emotional Wiring & Boundaries */}
                <div className="bg-astro-bg p-4 rounded-xl border border-astro-border space-y-1.5">
                  <span className="font-bold text-astro-primary flex items-center gap-1.5 text-xs uppercase tracking-wide">
                    <Heart className="w-4 h-4 text-astro-accent" />
                    {conversationalLang === 'hi' ? 'भावनात्मक संतुलन एवं संबंध' : (conversationalLang === 'hinglish' ? 'Emotional Boundaries & Trust Wiring' : 'Emotional Boundaries & Trust')}
                  </span>
                  <p className="text-[#384342] leading-relaxed text-xs sm:text-sm">
                    {humanizedNarrative.emotionalWiring[conversationalLang]}
                  </p>
                </div>

                {/* 4. Karmic Blindspot & Practical Fix */}
                <div className="bg-astro-bg p-4 rounded-xl border border-[#E2DED5] space-y-1.5 bg-astro-secondary-surface">
                  <span className="font-bold text-astro-secondary flex items-center gap-1.5 text-xs uppercase tracking-wide">
                    <AlertTriangle className="w-4 h-4 text-[#52635F]" />
                    {conversationalLang === 'hi' ? 'सावधानी एवं व्यावहारिक समाधान' : (conversationalLang === 'hinglish' ? 'Kahan Sambhalna Hai (Blindspot & Fix)' : 'Karmic Blindspot & Fix')}
                  </span>
                  <p className="text-[#384342] leading-relaxed text-xs sm:text-sm font-medium">
                    {humanizedNarrative.karmicBlindspotAndFix[conversationalLang]}
                  </p>
                </div>
              </div>

              {/* Dasha Phase Shift Callout Banner */}
              <div className="bg-astro-primary text-white p-4 sm:p-5 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#E6C687]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#E6C687]">
                      {conversationalLang === 'hi' ? 'दशा-आधारित जीवन रूपांतरण' : (conversationalLang === 'hinglish' ? 'Dasha-Driven Mindset & Leadership Shift' : 'Dasha-Driven Evolution')}
                    </span>
                  </div>
                  <p className="text-xs text-white/90">
                    {conversationalLang === 'hi'
                      ? 'जानिए कैसे आपकी सक्रिय महादशा आपकी निर्णय शैली और कार्य-दिशा को बदल रही है।'
                      : (conversationalLang === 'hinglish'
                          ? 'Is phase mein aapka orientation kya tha, aur agli Dasha ke sath leadership style kaise transform hoga — dekhein pura roadmap.'
                          : 'Explore how your leadership agency, risk appetite, and daily focus systematically shift across planetary Mahadashas.')}
                  </p>
                </div>
                <button
                  onClick={() => setActiveViewMode('dasha-evolution')}
                  className="bg-astro-accent hover:bg-[#9E824C] text-astro-primary font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-1.5 shrink-0 transition-all cursor-pointer shadow-xs font-sans"
                >
                  <span>{conversationalLang === 'hi' ? 'दशा रूपांतरण रोडमैप देखें' : (conversationalLang === 'hinglish' ? 'View Dasha Evolution' : 'Explore Life Phases')}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Seamless Multi-Paragraph Integrated Narrative (11 Synthesized Sections) */}
            <div className="space-y-6 text-sm text-[#384342] leading-relaxed font-normal">
              {integratedProfile.sections.map((section, idx) => (
                <div key={section.id} className="bg-[#FAF6ED]/70 p-6 sm:p-7 rounded-2xl border border-astro-border space-y-3.5">
                  <h4 className="text-base sm:text-lg font-bold font-serif text-astro-primary flex items-center gap-2.5">
                    {getProfileIcon(section.iconName)}
                    <span>{section.title}</span>
                  </h4>
                  <div className="text-[#384342] leading-relaxed whitespace-pre-line text-sm sm:text-base">
                    {section.content}
                  </div>
                </div>
              ))}

              {/* Natural In-Line Remedy Decision */}
              <div className="bg-astro-bg p-5 sm:p-6 rounded-2xl border border-emerald-300/70 text-xs sm:text-sm text-astro-text space-y-2">
                <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
                  <span>{isHi ? 'शास्त्रीय संतुलन एवं उपाय निर्णय' : 'Remedy Assessment & Practical Verdict'}</span>
                </div>
                <p className="text-[#384342] font-medium leading-relaxed">
                  {integratedProfile.remedyVerdict.text}
                </p>
              </div>
            </div>

            {/* Bottom CTA to Explore Specific Areas */}
            <div className="pt-6 border-t border-astro-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-xs text-astro-secondary-text">
                {isHi ? 'विशिष्ट जीवन क्षेत्र जैसे कैरियर, विवाह, संतान या वित्त पर गहरा परामर्श चाहते हैं?' : 'Want a specialized deep-dive into Career, Marriage, Progeny, or Wealth?'}
              </span>
              <button
                onClick={() => setActiveViewMode('theme-deep-dive')}
                className="px-5 py-2.5 bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-2 cursor-pointer transition-all transform hover:scale-105"
              >
                <Briefcase className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'विशिष्ट जीवन विषय का गहन परामर्श लें' : 'Explore a Specific Life Area'}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 2: ON-DEMAND INDIVIDUAL THEME DEEP-DIVE (Context-Aware Multi-Varga)*/}
      {/* ========================================================================= */}
      {activeViewMode === 'theme-deep-dive' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-8 shadow-md space-y-6">
            
            {/* Header */}
            <div className="border-b border-astro-border pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold text-astro-accent uppercase tracking-wider mb-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{isHi ? 'विशिष्ट जीवन क्षेत्र गहन अन्वेषण' : 'On-Demand Life Theme Deep-Dive'}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary">
                  {isHi ? 'जीवन विषयक संश्लेषित परामर्श एवं काल निर्णय' : 'Specialized Life Sector Synthesized Consultation'}
                </h3>
                <p className="text-xs sm:text-sm text-astro-secondary-text mt-1">
                  {isHi 
                    ? 'विषय का चयन करें और अपनी वर्तमान स्थिति (देश-काल-पात्र) के अनुसार समर्पित शास्त्रीय विश्लेषण प्राप्त करें।' 
                    : 'Select a life theme below. Each deep-dive dynamically activates its corresponding Vargas, house networks, Dasha activation, and future timing windows.'}
                </p>
              </div>

              <button
                onClick={() => setActiveViewMode('integrated-profile')}
                className="bg-[#FAF6ED] hover:bg-astro-border text-astro-primary border border-astro-border font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 shrink-0 cursor-pointer"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                <span>{isHi ? 'एकीकृत रिपोर्ट पर लौटें' : 'Back to Default Profile'}</span>
              </button>
            </div>

            {/* Life Theme Selection Bar */}
            <div className="flex bg-[#FAF6ED] p-1.5 rounded-2xl border border-astro-border overflow-x-auto gap-2">
              {[
                { id: 'child_development', label: isHi ? 'बाल विकास एवं शिक्षा (Child Dev)' : 'Child Development & Education', icon: Brain },
                { id: 'career', label: isHi ? 'कैरियर व आजीविका (Career)' : 'Career & Status', icon: Briefcase },
                { id: 'marriage', label: isHi ? 'विवाह व संबंध (Marriage)' : 'Marriage & Partnership', icon: Heart },
                { id: 'children', label: isHi ? 'संतान प्राप्ति व वंश (Progeny)' : 'Progeny & Conception (Parent Query)', icon: Users },
                { id: 'education', label: isHi ? 'उच्च शिक्षा (Higher Education)' : 'Higher Education & Research', icon: GraduationCap },
                { id: 'wealth', label: isHi ? 'धन व वित्त (Wealth)' : 'Wealth & Finance', icon: TrendingUp },
                { id: 'property', label: isHi ? 'अचल संपत्ति व वाहन (Property)' : 'Property & Conveyance', icon: Home },
                { id: 'health', label: isHi ? 'स्वास्थ्य व जीवन शक्ति (Health)' : 'Vitality & Wellness', icon: Activity },
                { id: 'spirituality', label: isHi ? 'अध्यात्म व धर्म (Spirituality)' : 'Spirituality & Dharma', icon: Sparkles },
                { id: 'travel', label: isHi ? 'विदेश यात्रा व स्थानांतरण (Travel)' : 'Travel & Relocation', icon: Globe },
                { id: 'business', label: isHi ? 'स्वतंत्र व्यापार (Business)' : 'Business & Commerce', icon: Flame },
                { id: 'politics', label: isHi ? 'लोक सेवा व नेतृत्व (Public Service)' : 'Public Service / Influence', icon: Award },
              ].map(theme => {
                const Icon = theme.icon;
                return (
                  <button
                    key={theme.id}
                    onClick={() => setSelectedDeepDiveTheme(theme.id as any)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                      selectedDeepDiveTheme === theme.id
                        ? 'bg-astro-primary text-white shadow-md'
                        : 'bg-astro-bg text-astro-secondary-text hover:bg-astro-border/70 border border-astro-border'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 text-astro-accent" />
                    <span>{theme.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Context / Desh-Kaal-Patra Input Panels for Selected Theme */}
            <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-2 text-xs">
                <span className="font-bold text-astro-primary flex items-center gap-1.5">
                  <Sliders className="w-4 h-4 text-astro-accent" />
                  {isHi ? 'आवश्यक संदर्भ व देश-काल-पात्र संरेखण (Prerequisite Context):' : 'Prerequisite Context & Desh-Kaal-Patra Alignment:'}
                </span>
                <span className="text-astro-secondary-text text-[11px] italic">
                  {isHi ? 'सटीक परिणाम हेतु वर्तमान स्थिति चुनें' : 'Refines astrological accuracy to your actual life stage'}
                </span>
              </div>

              {/* v5.12 CHILD DEVELOPMENT, EDUCATION & CAREER Context Inputs */}
              {selectedDeepDiveTheme === 'child_development' && (
                <div className="space-y-4 text-xs">
                  {/* Chart Context & Age Stage Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'कुंडली संदर्भ (Primary Subject):' : 'Chart Focus Context:'}</label>
                      <select
                        value={childChartContext}
                        onChange={(e) => setChildChartContext(e.target.value as any)}
                        className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                      >
                        <option value="child_own_chart">{isHi ? 'बच्चे की अपनी जन्म कुंडली (प्राथमिक)' : "Child's Own Kundali (Primary)"}</option>
                        <option value="parent_chart_inquiry">{isHi ? 'अभिभावक कुंडली (संतान भाव साक्ष्य)' : 'Parent Chart (Progeny Receptivity)'}</option>
                      </select>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-astro-secondary-text font-medium">{isHi ? 'आयु एवं विकास अवस्था:' : 'Age & Life-Stage Gate:'}</label>
                        {detectedAge !== null && (
                          <span className="bg-astro-primary text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
                            {detectedAge} {isHi ? 'वर्ष (स्वतः ज्ञात)' : 'yrs (Auto)'}
                          </span>
                        )}
                      </div>
                      <select
                        value={childStage}
                        onChange={(e) => setChildStage(e.target.value as any)}
                        className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                      >
                        <option value="early_childhood">{isHi ? 'प्रारंभिक बचपन (0–5 वर्ष: आधारभूत विकास)' : 'Early Childhood (0–5 yrs: Foundations)'}</option>
                        <option value="primary_school">{isHi ? 'प्राथमिक विद्यालय (6–10 वर्ष: बुनियादी शिक्षा)' : 'Primary School (6–10 yrs: Elementary)'}</option>
                        <option value="middle_secondary">{isHi ? 'माध्यमिक विद्यालय (11–15 वर्ष: विषय अभिरुचि)' : 'Middle / Secondary (11–15 yrs)'}</option>
                        <option value="higher_secondary">{isHi ? 'उच्च माध्यमिक (16–18 वर्ष: संकाय/प्रतियोगिता)' : 'Higher Secondary (16–18 yrs: Stream Prep)'}</option>
                        <option value="undergrad_college">{isHi ? 'स्नातक / कॉलेज (19–23 वर्ष: उच्च शिक्षा)' : 'College / Undergrad (19–23 yrs)'}</option>
                        <option value="postgrad_early_career">{isHi ? 'स्नातकोत्तर / प्रारंभिक कैरियर (24+ वर्ष)' : 'Postgrad / Early Career (24+ yrs)'}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'विकासात्मक काल सीमा:' : 'Developmental Horizon:'}</label>
                      <div className="flex gap-1.5">
                        {[
                          { id: '1yr', label: isHi ? '1 वर्ष' : '1 Year' },
                          { id: '3yr', label: isHi ? '3 वर्ष' : '3 Years' },
                          { id: '5yr', label: isHi ? '5 वर्ष' : '5 Years' },
                        ].map(h => (
                          <button
                            key={h.id}
                            onClick={() => setChildCareerHorizon(h.id as any)}
                            className={`flex-1 py-2 rounded-xl font-bold transition-all border ${childCareerHorizon === h.id ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'}`}
                          >
                            {h.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Sub-Theme Selector Chips */}
                  <div>
                    <label className="block text-astro-secondary-text font-medium mb-1.5">{isHi ? 'विशेष अन्वेषण उप-विषय (On-Demand Focus Dimension):' : 'On-Demand Developmental Sub-Theme:'}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { id: 'integrated', label: isHi ? 'समग्र एकीकृत विकास' : 'All-round Integrated Profile' },
                        { id: 'temperament', label: isHi ? 'स्वभाव व संवेदनशीलता' : 'Temperament & Emotional Style' },
                        { id: 'learning_style', label: isHi ? 'सीखने की शैली व अध्ययन' : 'Learning Style & Study Habits' },
                        { id: 'school_education', label: isHi ? 'स्कूली शिक्षा व विषय' : 'School Education & Strengths' },
                        { id: 'concentration', label: isHi ? 'एकाग्रता व निरंतरता' : 'Concentration & Focus' },
                        { id: 'competitive_exams', label: isHi ? 'प्रतियोगी परीक्षाएं' : 'Competitive Exam Readiness' },
                        { id: 'higher_education', label: isHi ? 'उच्च शिक्षा व विशेषज्ञता' : 'Higher Education Path' },
                        { id: 'foreign_education', label: isHi ? 'विदेश शिक्षा योग' : 'Foreign Education' },
                        { id: 'career_direction', label: isHi ? 'कैरियर दिशा व कार्यशैली' : 'Emerging Career Direction' },
                        { id: 'leadership', label: isHi ? 'नेतृत्व व रचनात्मकता' : 'Leadership & Creativity' },
                        { id: 'educational_timing', label: isHi ? 'दशा व शैक्षणिक काल' : 'Active Dasha & Timing' },
                        { id: 'timing_windows', label: isHi ? 'भविष्य विकासात्मक खिड़कियां' : '1/3/5-Yr Growth Horizons' },
                        { id: 'supportive_guidance', label: isHi ? 'सहायक मार्गदर्शन व उपाय' : 'Supportive Guidance' },
                        { id: 'gemstone_assessment', label: isHi ? 'रत्न अनुकूलता परीक्षा' : 'Gemstone Suitability' },
                      ].map(st => (
                        <button
                          key={st.id}
                          onClick={() => setChildSubTheme(st.id as any)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer border ${
                            childSubTheme === st.id
                              ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                              : 'bg-astro-bg text-astro-secondary-text border-astro-border hover:bg-astro-border/50'
                          }`}
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Age & Stage Gate Warning Banner */}
                  {isPrematureStage && isHigherStageQuery && (
                    <div className="bg-astro-secondary-surface p-3.5 rounded-xl border border-[#E2DED5] text-astro-secondary text-xs flex items-start gap-2.5 animate-fade-in">
                      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
                      <div>
                        <span className="font-bold block mb-0.5">
                          {isHi ? 'आयु व विकास अवस्था चेतावनी (Age/Stage Gate Advisory):' : 'Developmental Stage Gate Advisory:'}
                        </span>
                        <p className="text-[11px] leading-relaxed">
                          {isHi
                            ? 'इस प्रारंभिक आयु में उच्च शिक्षा अथवा निश्चित कैरियर का पूर्वानुमान लगाना शास्त्रीय व विकासात्मक दृष्टि से अनुचित है। बच्चे का मस्तिष्क निरंतर विकसित हो रहा है। नीचे प्रणाली इस आयु के अनुसार संज्ञानात्मक आदतों, अभिरुचियों और अध्ययन प्रवृत्ति का मार्गदर्शन प्रदान कर रही है। औपचारिक कैरियर संकाय चयन 15-18 वर्ष की आयु में सार्थक होता है।'
                            : 'At this developmental stage (Early Childhood / Primary School), rigid higher-education or vocational specialization forecasts are premature. Children evolve rapidly through cognitive maturation and nurturing. Below, the system analyzes foundational cognitive tendencies and learning habits, deferring definitive career track selection until senior secondary years (ages 15–18).'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Pediatric / Psychological Professional Boundary Disclaimer */}
                  <div className="bg-astro-bg p-3 rounded-xl border border-astro-border text-astro-secondary-text text-[11px] flex items-start gap-2">
                    <Shield className="w-4 h-4 shrink-0 text-astro-primary mt-0.5" />
                    <span>
                      {isHi
                        ? 'शास्त्रीय एवं चिकित्सीय सीमा प्रकटीकरण: ज्योतिषीय बाल विकास विश्लेषण विकासात्मक प्रवृत्तियों और सीखने की शैली को समझने का सहायक साधन है। यह किसी भी चिकित्सीय, बाल रोग, मनोवैज्ञानिक अथवा शैक्षिक विशेषज्ञ के निदान का विकल्प नहीं है।'
                        : 'Professional Boundary Notice: Astrological child development analysis highlights symbolic learning styles and behavioral tendencies. It does not constitute pediatric, psychological, or psychiatric diagnosis, nor a deterministic guarantee of academic outcomes.'}
                    </span>
                  </div>
                </div>
              )}

              {/* CAREER Context Inputs */}
              {selectedDeepDiveTheme === 'career' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'वर्तमान कैरियर स्थिति:' : 'Current Career Stage:'}</label>
                    <select
                      value={careerStage}
                      onChange={(e) => setCareerStage(e.target.value as any)}
                      className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                    >
                      <option value="student">{isHi ? 'विद्यार्थी / प्रवेश स्तर (Student)' : 'Student / Entry-Level'}</option>
                      <option value="job_seeker">{isHi ? 'रोजगार खोजकर्ता (Job Seeker)' : 'Actively Seeking Employment'}</option>
                      <option value="employed">{isHi ? 'कार्यरत पेशेवर (Employed Professional)' : 'Employed Professional'}</option>
                      <option value="business">{isHi ? 'उद्यमी / व्यापारी (Business Owner)' : 'Business Owner / Entrepreneur'}</option>
                      <option value="executive">{isHi ? 'वरिष्ठ अधिकारी / नेतृत्व (Senior Executive)' : 'Senior Executive / Leadership'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'पूर्वानुमान काल सीमा:' : 'Forecast Horizon Window:'}</label>
                    <div className="flex gap-2">
                      {[
                        { id: '1yr', label: isHi ? '1 वर्ष' : '1 Year' },
                        { id: '3yr', label: isHi ? '3 वर्ष' : '3 Years' },
                        { id: '5yr', label: isHi ? '5 वर्ष' : '5 Years' },
                      ].map(h => (
                        <button
                          key={h.id}
                          onClick={() => setCareerHorizon(h.id as any)}
                          className={`flex-1 py-2 rounded-xl font-bold transition-all border ${careerHorizon === h.id ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'}`}
                        >
                          {h.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* MARRIAGE Context Inputs */}
              {selectedDeepDiveTheme === 'marriage' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'वैवाहिक स्थिति:' : 'Marital Status / Inquiry Context:'}</label>
                    <select
                      value={maritalStatus}
                      onChange={(e) => setMaritalStatus(e.target.value as any)}
                      className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                    >
                      <option value="unmarried">{isHi ? 'अविवाहित (Unmarried / Looking)' : 'Unmarried (Seeking Alliance)'}</option>
                      <option value="married">{isHi ? 'विवाहित (Married)' : 'Married (Seeking Harmony / Growth)'}</option>
                      <option value="separated">{isHi ? 'तलाकशुदा / पृथक (Separated)' : 'Separated / Remarriage Query'}</option>
                      <option value="future_query">{isHi ? 'भविष्य संबंध प्रश्न (Relationship Query)' : 'Future Relationship Dynamics'}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 bg-astro-bg p-3 rounded-xl border border-astro-border">
                    <Heart className="w-5 h-5 text-astro-accent" />
                    <span className="text-[11px] text-astro-secondary-text">
                      {isHi ? 'सप्तम भाव, नवांश (D9), शुक्र/गुरु एवं आगामी गोचर के आधार पर भविष्य खिड़कियों का विश्लेषण।' : 'Evaluating 7th house, D9 Navamsha, Venus/Jupiter karakas, and dynamic timing windows.'}
                    </span>
                  </div>
                </div>
              )}

              {/* CHILDREN Context Inputs with Mandatory Partner Chart Disclaimer */}
              {selectedDeepDiveTheme === 'children' && (
                <div className="space-y-3 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'वैवाहिक स्थिति:' : 'Marital Status:'}</label>
                      <select
                        value={maritalStatus}
                        onChange={(e) => setMaritalStatus(e.target.value as any)}
                        className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                      >
                        <option value="married">{isHi ? 'विवाहित (Married)' : 'Married'}</option>
                        <option value="unmarried">{isHi ? 'अविवाहित (Unmarried / Future Query)' : 'Unmarried / Future Planning'}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'जीवनसाथी की कुंडली उपलब्ध है?' : 'Spouse / Partner Kundali Available?'}</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSpouseKundaliAvailable(false)}
                          className={`flex-1 py-2 rounded-xl font-bold transition-all border ${!spouseKundaliAvailable ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'}`}
                        >
                          {isHi ? 'एकल कुंडली (Single Chart)' : 'Single Chart (Indicative)'}
                        </button>
                        <button
                          onClick={() => setSpouseKundaliAvailable(true)}
                          className={`flex-1 py-2 rounded-xl font-bold transition-all border ${spouseKundaliAvailable ? 'bg-astro-primary text-white border-astro-primary' : 'bg-astro-bg text-astro-secondary-text border-astro-border'}`}
                        >
                          {isHi ? 'दोनों कुंडलियां (Spouse Available)' : 'Dual Chart Comparison'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {!spouseKundaliAvailable && (
                    <div className="bg-astro-secondary-surface p-3 rounded-xl border border-[#E2DED5] text-astro-secondary text-[11px] flex items-start gap-2">
                      <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        {isHi 
                          ? 'शास्त्रीय सीमा प्रकटीकरण: पूर्ण संतान निर्णय हेतु दोनों जीवनसाथियों (D1 एवं D7 सप्तमांश) का मिलान अनिवार्य होता है। यह एकल कुंडली के आधार पर केवल सांकेतिक विश्लेषण है।' 
                          : 'Classical Limitation Disclosure: Complete progeny judgment requires comparative analysis of both partners (D1 and D7 Saptamsha). This single-chart reading provides an indicative natal baseline.'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* HIGHER EDUCATION Context Inputs */}
              {selectedDeepDiveTheme === 'education' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block text-astro-secondary-text font-medium mb-1">{isHi ? 'शिक्षा का स्तर:' : 'Target Education Stage:'}</label>
                    <select
                      value={educationStage}
                      onChange={(e) => setEducationStage(e.target.value as any)}
                      className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary"
                    >
                      <option value="school">{isHi ? 'माध्यमिक / 10+2 (School / Intermediate)' : 'Secondary / High School'}</option>
                      <option value="undergrad">{isHi ? 'स्नातक (Undergraduate Degree)' : 'Undergraduate Degree'}</option>
                      <option value="postgrad">{isHi ? 'स्नातकोत्तर (Postgraduate / Master)' : 'Postgraduate Degree (Master)'}</option>
                      <option value="research_competitive">{isHi ? 'शोध / पीएचडी / प्रतियोगी परीक्षा (PhD / Competitive Exam)' : 'Doctorate / Research / Civil Services'}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 bg-astro-bg p-3 rounded-xl border border-astro-border">
                    <GraduationCap className="w-5 h-5 text-astro-accent" />
                    <span className="text-[11px] text-astro-secondary-text">
                      {isHi ? 'चतुर्थ (मूल ज्ञान), पंचम (बुद्धि), नवम (उच्च विद्या) एवं सिद्धांश (D24) का समन्वय।' : 'Cross-validating 4th, 5th, 9th houses and D24 Siddhamsa for academic culmination.'}
                    </span>
                  </div>
                </div>
              )}

              {/* HEALTH Context Disclaimer */}
              {selectedDeepDiveTheme === 'health' && (
                <div className="bg-astro-secondary-surface p-3 rounded-xl border border-[#E2DED5] text-astro-secondary text-[11px] flex items-start gap-2">
                  <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {isHi 
                      ? 'चिकित्सीय अस्वीकरण: ज्योतिषीय स्वास्थ्य विश्लेषण केवल शारीरिक प्रवृत्तियों और ऊर्जा संतुलन को समझने का साधन है। इसे चिकित्सीय निदान अथवा उपचार का विकल्प न मानें।' 
                      : 'Medical Disclaimer: Astrological wellness insights identify constitutional tendencies and energetic balance. They do not constitute medical diagnosis, clinical prognosis, or treatment.'}
                  </span>
                </div>
              )}
            </div>

            {/* Substantive Theme Deep-Dive Analysis Content */}
            <div className="bg-[#FAF6ED]/70 p-6 sm:p-7 rounded-3xl border border-astro-border space-y-5">
              
              {/* Active Multi-Varga Evidence Summary */}
              <div className="bg-astro-bg p-3.5 rounded-xl border border-astro-border flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-astro-accent" />
                  <span className="font-bold text-astro-primary">{isHi ? 'सक्रिय वर्ग एवं साक्ष्य संजाल:' : 'Integrated Evidence Network:'}</span>
                  <span className="text-astro-secondary-text">
                    {selectedDeepDiveTheme === 'child_development' && `D1 (Lagna ${lagnaHouse?.sign}, Moon ${moonPlanet?.sign}, 2nd, 4th, 5th, 9th, 10th) + D24 Siddhamsa + D10 Dashamsha + ${fourthSAV}/${fifthSAV}/${tenthSAV} SAV Bindus + ${currentDasha?.planet} MD`}
                    {selectedDeepDiveTheme === 'career' && `D1 (10th ${tenthHouse?.sign}, 2nd, 6th, 11th) + D10 Dashamsha + ${tenthSAV} SAV Bindus + ${currentDasha?.planet} MD`}
                    {selectedDeepDiveTheme === 'marriage' && `D1 (7th ${seventhHouse?.sign}, lord ${seventhHouse?.signLord}) + D9 Navamsha + Venus/Jupiter + ${seventhSAV} SAV`}
                    {selectedDeepDiveTheme === 'children' && `D1 (5th ${fifthHouse?.sign}, lord ${fifthHouse?.signLord}) + D7 Saptamsha + Jupiter Karaka + ${fifthSAV} SAV`}
                    {selectedDeepDiveTheme === 'education' && `D1 (4th/5th/9th Houses) + D24 Siddhamsa + Mercury/Jupiter + ${fifthSAV} SAV`}
                    {selectedDeepDiveTheme === 'wealth' && `D1 (2nd ${secondHouse?.sign}, 11th ${eleventhHouse?.sign}) + D2 Hora + Dhana Yogas + ${secondSAV}/${eleventhSAV} SAV`}
                    {selectedDeepDiveTheme === 'property' && `D1 (4th ${fourthHouse?.sign}, Mars/Venus) + D4 Chaturthamsha + D16 Shodashamsha + ${fourthSAV} SAV`}
                    {selectedDeepDiveTheme === 'health' && `D1 (1st, 6th, 8th, 12th Houses) + Lagna Lord + Sun/Moon vitality + ${lagnaSAV} SAV`}
                    {selectedDeepDiveTheme === 'spirituality' && `D1 (9th, 12th Houses) + D20 Vimsamsha + Jupiter/Ketu + Dasha Activation`}
                    {selectedDeepDiveTheme === 'travel' && `D1 (3rd, 9th, 12th Houses) + Rahu/Moon + Gochar Transits`}
                    {selectedDeepDiveTheme === 'business' && `D1 (2nd, 7th, 10th, 11th) + D10 Dashamsha + Mercury + Dhana Yogas`}
                    {selectedDeepDiveTheme === 'politics' && `D1 (1st, 5th, 9th, 10th) + Sun/Mars/Rahu + Desh-Kaal-Patra Context`}
                  </span>
                </div>
                <span className="bg-astro-border text-astro-primary px-2.5 py-1 rounded font-semibold text-[11px]">
                  Parashari Multi-Chart Validation
                </span>
              </div>

              {/* Substantive Interpretation Text */}
              <div className="space-y-4 text-sm text-[#384342] leading-relaxed">
                
                {/* v5.12 CHILD DEVELOPMENT, EDUCATION & CAREER Deep-Dive */}
                {selectedDeepDiveTheme === 'child_development' && (
                  <div className="space-y-5">
                    {/* Header */}
                    <div className="border-b border-astro-border pb-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-base sm:text-lg font-bold font-serif text-astro-primary flex items-center gap-2">
                          <Brain className="w-5 h-5 text-astro-accent" />
                          <span>
                            {isHi 
                              ? `बाल विकास, शिक्षा एवं अभिरुचि परामर्श (${childCareerHorizon} विकासात्मक परिप्रेक्ष्य)`
                              : `Child Development, Learning & Emerging Direction (${childCareerHorizon} Horizon)`}
                          </span>
                        </h4>
                        <span className="text-xs bg-astro-primary text-white px-3 py-1 rounded-full font-medium">
                          {childStage === 'early_childhood' && (isHi ? 'प्रारंभिक बचपन (0–5 वर्ष)' : 'Early Childhood (0–5 yrs)')}
                          {childStage === 'primary_school' && (isHi ? 'प्राथमिक विद्यालय (6–10 वर्ष)' : 'Primary School (6–10 yrs)')}
                          {childStage === 'middle_secondary' && (isHi ? 'माध्यमिक विद्यालय (11–15 वर्ष)' : 'Middle / Secondary (11–15 yrs)')}
                          {childStage === 'higher_secondary' && (isHi ? 'उच्च माध्यमिक (16–18 वर्ष)' : 'Higher Secondary (16–18 yrs)')}
                          {childStage === 'undergrad_college' && (isHi ? 'कॉलेज / स्नातक (19–23 वर्ष)' : 'College / Undergrad (19–23 yrs)')}
                          {childStage === 'postgrad_early_career' && (isHi ? 'प्रारंभिक कैरियर (24+ वर्ष)' : 'Postgrad / Early Career (24+ yrs)')}
                        </span>
                      </div>
                      <p className="text-xs text-astro-secondary-text mt-1">
                        {isHi
                          ? 'लग्न (व्यक्तित्व), चंद्र (मन), बुध (बुद्धि), गुरु (ज्ञान), चतुर्थ/पंचम भाव एवं सिद्धांश (D24) का संश्लेषित गैर-नियतिवादी विश्लेषण।'
                          : 'Non-deterministic synthesis of Lagna, Moon (temperament), Mercury (cognition), Jupiter (wisdom), 4th/5th houses, and D24 Siddhamsa.'}
                      </p>
                    </div>

                    {/* Section A: Childhood Temperament & Emotional Sensitivity */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'temperament') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-2">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <Heart className="w-4 h-4 text-astro-accent" />
                          {isHi ? '1. बाल स्वभाव, संवेदनशीलता एवं सामाजिक व्यवहार' : '1. Childhood Temperament, Emotional Sensitivity & Social Style'}
                        </h5>
                        <p>
                          {isHi
                            ? `लग्न स्वामी (${lagnaHouse?.signLord}) और चंद्रमा (${moonPlanet?.sign}) का समन्वय यह दर्शाता है कि बच्चा जिज्ञासु, संवेदनशील और अवलोकन में कुशल है। नवीन वातावरण में बच्चा पहले शांतिपूर्वक निरीक्षण करता है और फिर आत्मविश्वास से जुड़ता है। अत्यधिक दबाव या कठोर तुलनात्मक माहौल से बचने और सकारात्मक प्रोत्साहन देने से बच्चे की सीखने की स्वाभाविक इच्छा शक्ति तीव्र होती है।`
                            : `The temperament matrix (anchored by Lagna lord ${lagnaHouse?.signLord} and Moon in ${moonPlanet?.sign}) indicates an observant, receptive, and emotionally thoughtful native. In unfamiliar environments, the child naturally processes cues before actively participating. Emotional security and encouraging validation serve as the primary fuel for self-expression, whereas high-pressure comparisons may trigger temporary withdrawal.`}
                        </p>
                      </div>
                    )}

                    {/* Section B: Cognitive Architecture & Learning Style */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'learning_style') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-2">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <Brain className="w-4 h-4 text-astro-accent" />
                          {isHi ? '2. संज्ञानात्मक शैली, अधिगम प्रवृत्ति एवं अध्ययन की आदतें' : '2. Cognitive Architecture, Learning Style & Study Habits'}
                        </h5>
                        <p>
                          {isHi
                            ? `बुध (${mercuryPlanet?.sign}) और पंचम भाव (${fifthHouse?.sign}, ${fifthSAV} बिंदु) की स्थिति यह दर्शाती है कि बच्चे की बुद्धि दृश्य (Visual) एवं अवधारणात्मक (Conceptual) माध्यमों से सबसे तीव्रता से ग्रहण करती है। रटने की अपेक्षा 'कारण और प्रभाव' समझकर पढ़ने पर विषय दीर्घकाल तक स्मरण रहता है। अध्ययन कक्ष में शांत वातावरण और व्यवस्थित अध्ययन समय सारिणी बौद्धिक एकाग्रता को कई गुना बढ़ाती है।`
                            : `Cognitive processing (governed by Mercury in ${mercuryPlanet?.sign} and the 5th house in ${fifthHouse?.sign} with ${fifthSAV} SAV bindus) thrives on visual and conceptual frameworks rather than mechanical rote memorization. Understanding 'why' behind principles creates deep neural retention. A dedicated, clutter-free study space combined with predictable study intervals optimizes focus endurance.`}
                        </p>
                      </div>
                    )}

                    {/* Section C: School Education & Academic Strengths */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'school_education' || childSubTheme === 'concentration') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-2">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-astro-accent" />
                          {isHi ? '3. स्कूली शिक्षा, शैक्षणिक क्षमताएं एवं एकाग्रता' : '3. School Education, Academic Strengths & Focus Endurance'}
                        </h5>
                        <p>
                          {isHi
                            ? `चतुर्थ भाव (${fourthHouse?.sign}, ${fourthSAV} बिंदु) और गुरु का प्रभाव शैक्षणिक विषयों में स्वाभाविक रुचि पैदा करता है। विश्लेषणात्मक विषयों, भाषाओं और व्यावहारिक विज्ञान में बच्चे की समझ मजबूत है। पंचम भाव में ${fifthSAV} अष्टकवर्ग बिंदु यह सिद्ध करते हैं कि अभ्यास में निरंतरता रहने पर परीक्षा परिणामों में लगातार सुधार होगा।`
                            : `Foundational academic indicators (4th house in ${fourthHouse?.sign} with ${fourthSAV} SAV bindus synthesized with Jupiter's benevolent aspect) foster healthy scholastic absorption. The native displays natural aptitude for analytical reasoning, structured languages, and applied sciences. Consistent revision rhythms unlock high academic milestones.`}
                        </p>
                      </div>
                    )}

                    {/* Section D: Higher Education, Competitive Exams & Emerging Career Directions */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'higher_education' || childSubTheme === 'career_direction' || childSubTheme === 'competitive_exams' || childSubTheme === 'foreign_education' || childSubTheme === 'leadership') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-3">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-astro-accent" />
                          {isHi ? '4. उच्च शिक्षा, प्रतियोगी परीक्षा व उभरती कैरियर दिशाएं' : '4. Higher Learning, Competitive Orientation & Vocational Potential'}
                        </h5>
                        
                        {isPrematureStage ? (
                          <div className="bg-[#FAF6ED] p-3 rounded-xl border border-astro-border text-xs text-astro-secondary-text space-y-1.5">
                            <span className="font-semibold text-astro-primary block">
                              {isHi ? 'विकासात्मक चरण अनुकूल दृष्टिकोण:' : 'Stage-Appropriate Orientation:'}
                            </span>
                            <p>
                              {isHi
                                ? `चूंकि बच्चा अभी प्राथमिक/बुनियादी विकास चरण में है, विशिष्ट कैरियर या स्ट्रीम तय करने के बजाय बहुआयामी प्रतिभा को बढ़ावा देना चाहिए। नवम भाव, दशम भाव (${tenthSAV} बिंदु) और दशमांश (D10) के प्रारंभिक रुझान बताते हैं कि भविष्य में विश्लेषणात्मक प्रबंधन, तकनीकी अनुसंधान, नीति-निर्माण अथवा रचनात्मक उद्यम में उच्च क्षमता रहेगी। निश्चित विषय चुनाव 15-18 वर्ष की आयु में स्पष्ट होगा।`
                                : `Because the subject is in early foundational years, parents should nurture multidimensional curiosity rather than forcing early specialization. Natal archetypes (anchored by 10th house with ${tenthSAV} SAV bindus, D10 Dashamsha, and D24 Siddhamsa) indicate future alignment with analytical systems, technology/research, strategic governance, or creative enterprise. Formal career stream crystallization is best evaluated around ages 15–18.`}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2 text-xs">
                            <p>
                              {isHi
                                ? `दशम भाव (${tenthHouse?.sign}), स्वामी ${tenthHouse?.signLord} और सिद्धांश (D24) व दशमांश (D10) के समन्वय से प्रमुख व्यावसायिक क्षेत्र: (1) विश्लेषणात्मक व तकनीकी क्षेत्र (Technology / Data / Engineering), (2) संगठनात्मक प्रबंधन व वित्त (Strategic Management / Finance), (3) उच्च शोध एवं शिक्षण (Research / Academia)। प्रतियोगी परीक्षाओं में सफलता हेतु व्यवस्थित समय प्रबंधन एवं नियमित मॉक टेस्ट आवश्यक हैं।`
                                : `Vocational synthesis (10th house in ${tenthHouse?.sign}, lord ${tenthHouse?.signLord}, cross-referenced with D24 Siddhamsa and D10 Dashamsha) outlines prominent trajectories: (1) Analytical Systems & Applied Technology, (2) Strategic Governance, Administration & Finance, (3) Advanced Research & Higher Academia. Competitive exam success is supported through timed simulations and stress-mitigated study schedules.`}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Section E: Active Educational Timing & Growth Horizons */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'educational_timing' || childSubTheme === 'timing_windows') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-2">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <Clock className="w-4 h-4 text-astro-accent" />
                          {isHi ? `5. सक्रिय दशा एवं ${childCareerHorizon} विकासात्मक कालखंड (${currentMonthYearStr} से)` : `5. Active Timing & ${childCareerHorizon} Growth Windows (From ${currentMonthYearStr})`}
                        </h5>
                        <p>
                          {isHi
                            ? `वर्तमान में सक्रिय ${currentDasha?.planet} महादशा एवं ${activeAntardasha?.planet || 'सक्रिय'} अंतर्दशा का प्रभाव बच्चे के सीखने और नए कौशलों को आत्मसात करने के लिए अत्यंत उर्वर काल है। आगामी ${childCareerHorizon} के दौरान ज्ञानार्जन के नए अवसर, स्कूली मान्यता और रचनात्मक आत्मविश्वास में सतत वृद्धि होगी।`
                            : `The operating ${currentDasha?.planet} Mahadasha and active ${activeAntardasha?.planet || 'current'} Antardasha establish a productive cognitive absorption cycle. Over the chosen ${childCareerHorizon} horizon, planetary transits stimulate scholastic milestones, extracurricular confidence, and progressive conceptual clarity.`}
                        </p>
                      </div>
                    )}

                    {/* Section F: Supportive Guidance Suite */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'supportive_guidance') && (
                      <div className="bg-[#FAF6ED] p-4 sm:p-5 rounded-2xl border border-astro-accent/40 space-y-3">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-astro-accent" />
                          {isHi ? '6. सहायक मार्गदर्शन, अध्ययन वातावरण एवं अभिभावक परामर्श' : '6. Supportive Guidance: Learning Strategies, Environment & Parenting'}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border">
                            <span className="font-bold text-astro-primary block mb-1">
                              {isHi ? 'अध्ययन रणनीति एवं दिनचर्या:' : 'Learning Strategy & Study Rhythm:'}
                            </span>
                            <p className="text-astro-secondary-text">
                              {isHi
                                ? 'छोटे-छोटे अध्ययन खंड (25-30 मिनट) और बीच में 5 मिनट का विराम अपनाएं। दृश्य आरेखों (Mind Maps) का उपयोग करें।'
                                : 'Implement interval-based study sessions (25–30 min focus blocks) with micro-breaks. Incorporate visual mind maps and diagrammatic notes.'}
                            </p>
                          </div>
                          <div className="bg-astro-bg p-3 rounded-xl border border-astro-border">
                            <span className="font-bold text-astro-primary block mb-1">
                              {isHi ? 'संवाद एवं प्रोत्साहन शैली:' : 'Parental Communication Style:'}
                            </span>
                            <p className="text-astro-secondary-text">
                              {isHi
                                ? 'प्रयास और सीखने की प्रक्रिया की प्रशंसा करें। गलतियों को सीखने का स्वाभाविक हिस्सा बनाएं।'
                                : 'Praise genuine effort and persistence rather than grades alone. Foster an open dialogue where curiosity is celebrated without fear of mistakes.'}
                            </p>
                          </div>
                        </div>

                        {/* Remedial Assessment */}
                        <div className="bg-astro-bg p-3 rounded-xl border border-astro-border text-xs">
                          <span className="font-bold text-astro-primary block mb-1">
                            {isHi ? 'शास्त्रीय उपाय मूल्यांकन (Remedial Harmony):' : 'Astrological Harmony & Balance Assessment:'}
                          </span>
                          <p className="text-astro-secondary-text">
                            <span className="font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded mr-1.5">
                              {isHi ? 'उपाय: किसी विशिष्ट कर्मकांडी उपाय की आवश्यकता नहीं है।' : 'Remedy: None specifically required at this time.'}
                            </span>
                            {isHi
                              ? 'घर में प्रेमपूर्ण व शांत वातावरण, नियमित दिनचर्या, सरस्वती वंदना अथवा गायत्री मंत्र का शांत स्मरण और सकारात्मक अभिभावक प्रोत्साहन संपूर्ण शास्त्रीय व मानसिक संतुलन हेतु पूर्णतः पर्याप्त है।'
                              : 'A peaceful home sanctuary, consistent sleep hygiene, regular outdoor physical activity, and gentle recital of Saraswati Vandana or Gayatri Mantra provide optimal energetic harmony.'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Section G: Gemstone Suitability Assessment for Children */}
                    {(childSubTheme === 'integrated' || childSubTheme === 'gemstone_assessment') && (
                      <div className="bg-astro-bg p-4 sm:p-5 rounded-2xl border border-astro-border space-y-2">
                        <h5 className="font-bold font-serif text-sm text-astro-primary flex items-center gap-2">
                          <Shield className="w-4 h-4 text-astro-accent" />
                          {isHi ? '7. बाल रत्न अनुकूलता परीक्षा (Strict Gemstone Assessment)' : '7. Strict Gemstone Suitability Assessment'}
                        </h5>
                        <div className="bg-astro-secondary-surface/60 p-3 rounded-xl border border-[#E2DED5]/70 text-xs text-astro-secondary space-y-1.5">
                          <span className="font-bold block">
                            {isHi ? 'रत्न निर्णय: वर्तमान में बच्चों के लिए किसी रत्न की अनुशंसा नहीं है।' : 'Verdict: No specific gemstone recommendation at this time.'}
                          </span>
                          <p className="text-[11px] leading-relaxed text-astro-secondary-text">
                            {isHi
                              ? 'शास्त्रीय सिद्धांत के अनुसार, बढ़ते बच्चों की ऊर्जा प्रणाली निरंतर विकसित हो रही होती है। बच्चों के लिए रत्न कभी भी स्वतः या अनिवार्य नहीं होते। प्राकृतिक पोषण, खुला खेल-कूद, स्नेहपूर्ण प्रोत्साहन और ज्ञानवर्धक वातावरण रत्नों की तुलना में कहीं अधिक शक्तिशाली व सुरक्षित हैं।'
                              : 'Under classical Parashari guidelines, young natives possess an evolving energetic constitution. Gemstones are never an automatic prescription for children. Wholesome nutrition, active outdoor play, emotional security, and structured learning provide far superior, risk-free developmental vitality without gemstone amplification.'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* CAREER Deep-Dive */}
                {selectedDeepDiveTheme === 'career' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? `कैरियर एवं व्यावसायिक वृद्धि विश्लेषण (${careerHorizon} कालखंड)` : `Comprehensive Career & Vocational Analysis (${careerHorizon} Horizon)`}
                    </h4>
                    <p>
                      {isHi
                        ? `दशम भाव (${tenthHouse?.sign}) और उसके स्वामी ${tenthHouse?.signLord} का दशमांश (D10) के साथ समन्वय यह दर्शाता है कि आपकी व्यावसायिक क्षमता संगठित, योजनाबद्ध और उत्तरदायित्वपूर्ण क्षेत्रों में सर्वाधिक फलदायी है। दशम भाव में ${tenthSAV} अष्टकवर्ग बिंदु एवं एकादश भाव में ${eleventhSAV} बिंदु यह सुनिश्चित करते हैं कि आपके प्रयासों का आर्थिक प्रतिफल समय के साथ स्थिर और संतोषजनक रहेगा। ${careerStage === 'business' ? 'स्वतंत्र व्यवसाय में आपको साझेदारों और वित्तीय अनुबंधों में पारदर्शिता रखनी चाहिए।' : 'नौकरी/पेशेवर क्षेत्र में आप रणनीतिक भूमिकाओं और नेतृत्व में उत्कृष्ट प्रदर्शन करेंगे।'}`
                        : `The vocational matrix (governed by the 10th house in ${tenthHouse?.sign}, lord ${tenthHouse?.signLord}, synthesized with D10 Dashamsha) favors structural environments demanding methodical execution, systems governance, and strategic oversight. Backed by ${tenthSAV} SAV bindus in the 10th house and ${eleventhSAV} in the 11th house, career efforts compound into stable authority. ${careerStage === 'business' ? 'In entrepreneurial ventures, prioritize transparent contract structures and cash-flow discipline.' : 'Within corporate or institutional hierarchies, your strength lies in cross-functional coordination and long-term project leadership.'}`}
                    </p>
                    <p>
                      {isHi
                        ? `आगामी ${careerHorizon} के दौरान, ${currentDasha?.planet} महादशा का प्रभाव नए अवसरों और कार्यक्षेत्र में विस्तार का संकेत देता है। विशेषकर गुरु एवं शनि के अनुकूल गोचर काल में पदोन्नति और नई जिम्मेदारियों के योग बनेंगे।`
                        : `Over the selected ${careerHorizon} horizon, the activation of ${currentDasha?.planet} Mahadasha stimulates career consolidation. Favorable transits of Jupiter and Saturn create designated growth windows for professional advancement and expanded scope.`}
                    </p>

                    <div className="pt-4 border-t border-astro-border/60">
                      <CareerBusinessVsJobModule kundali={kundali} lang={lang} />
                    </div>
                  </>
                )}

                {/* MARRIAGE Deep-Dive */}
                {selectedDeepDiveTheme === 'marriage' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'विवाह, वैवाहिक सामंजस्य एवं संबंध निर्णय' : 'Marriage, Partnership Dynamics & Prospective Windows'}
                    </h4>
                    <p>
                      {isHi
                        ? `सप्तम भाव (${seventhHouse?.sign}) और नवांश (D9) की स्थिति से यह स्पष्ट है कि आपका जीवनसाथी विचारशील, संस्कारी और व्यावहारिक बुद्धि वाला होगा। सप्तम भाव में ${seventhSAV} अष्टकवर्ग बिंदु संबंध में स्थिरता और परस्पर सहयोग की पुष्टि करते हैं। ${maritalStatus === 'unmarried' ? 'विवाह हेतु आगामी अनुकूल कालखंड में योग्य प्रस्ताव प्राप्त होने के प्रबल योग हैं।' : 'वैवाहिक जीवन में समय-समय पर स्पष्ट संवाद बनाए रखना संबंधों को और अधिक मधुर बनाएगा।'}`
                        : `The partnership axis (7th house in ${seventhHouse?.sign}, lord ${seventhHouse?.signLord}) validated across the D9 Navamsha indicates an intellectual, grounded, and supportive partner. Bolstered by ${seventhSAV} SAV bindus in the 7th house, marital foundations emphasize mutual respect and long-term stability. ${maritalStatus === 'unmarried' ? 'Prospective alliance windows will emerge during auspicious Jovian transits over the 7th/11th houses.' : 'In ongoing married life, cultivating transparent dialogue sustains enduring intimacy.'}`}
                    </p>
                  </>
                )}

                {/* CHILDREN Deep-Dive */}
                {selectedDeepDiveTheme === 'children' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'संतान सुख, वंश वृद्धि एवं सप्तमांश (D7) साक्ष्य' : 'Children, Progeny Indications & D7 Saptamsha Evidence'}
                    </h4>
                    <p>
                      {isHi
                        ? `पंचम भाव (${fifthHouse?.sign}), उसके स्वामी ${fifthHouse?.signLord} और संतान कारक गुरु की स्थिति आपकी कुंडली में संतान के प्रति स्नेह और शुभता को दर्शाती है। पंचम भाव में ${fifthSAV} बिंदु एवं सप्तमांश (D7) के साक्ष्य पारिवारिक सुख की ओर संकेत करते हैं। ${!spouseKundaliAvailable ? '(नोट: पूर्ण निर्णय हेतु जीवनसाथी की कुंडली के साथ मिलान अनुशंसित है)।' : 'दोनों कुंडलियों का संयुक्त मिलान संतान सुख के लिए अत्यधिक अनुकूल आधार प्रदान करता है।'}`
                        : `Progeny prospects (governed by the 5th house in ${fifthHouse?.sign}, lord ${fifthHouse?.signLord}, supported by Jupiter and D7 Saptamsha) reflect positive natal receptivity. Reinforced by ${fifthSAV} SAV bindus in the 5th house, parenting indicators highlight educational support and deep filial affection. ${!spouseKundaliAvailable ? '(Note: Full timeline optimization requires comparative evaluation with spouse Kundali).' : 'Combined chart analysis reveals harmonious familial expansion potential.'}`}
                    </p>
                  </>
                )}

                {/* HIGHER EDUCATION Deep-Dive */}
                {selectedDeepDiveTheme === 'education' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'उच्च शिक्षा, बौद्धिक विकास एवं सिद्धांश (D24) विश्लेषण' : 'Higher Education, Research & D24 Siddhamsa Synthesis'}
                    </h4>
                    <p>
                      {isHi
                        ? `चतुर्थ (आधारभूत विद्या), पंचम (प्रज्ञा) और नवम (उच्च ज्ञान) के स्वामियों का संबंध सिद्धांश (D24) में यह सिद्ध करता है कि आपकी बौद्धिक क्षमता उच्च डिग्री, शोध अथवा तकनीकी विशेषज्ञता के लिए अत्यंत उपयुक्त है। बुध और गुरु का शुभ प्रभाव ज्ञानार्जन में निरंतर सफलता प्रदान करता है।`
                        : `Academic pursuits (governed by 4th, 5th, and 9th houses synthesized in D24 Siddhamsa) indicate strong aptitude for advanced specialization, analytical research, and professional certifications. The harmonious disposition of Mercury and Jupiter sustains disciplined cognitive retention.`}
                    </p>
                  </>
                )}

                {/* WEALTH Deep-Dive */}
                {selectedDeepDiveTheme === 'wealth' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'धन संचय, आर्थिक समृद्धि एवं होरा (D2) साक्ष्य' : 'Wealth Accumulation, Financial Security & D2 Hora Analysis'}
                    </h4>
                    <p>
                      {isHi
                        ? `द्वितीय भाव (संचित धन: ${secondHouse?.sign}, ${secondSAV} बिंदु) और एकादश भाव (आय व लाभ: ${eleventhHouse?.sign}, ${eleventhSAV} बिंदु) का परस्पर संबंध होरा (D2) में मजबूत धन योग बनाता है। आपके पास आय के एक से अधिक स्रोत विकसित करने और दीर्घकालिक निवेश से संपत्ति बढ़ाने की क्षमता है।`
                        : `Financial foundations (2nd house in ${secondHouse?.sign} with ${secondSAV} bindus, 11th house in ${eleventhHouse?.sign} with ${eleventhSAV} bindus) cross-referenced in D2 Hora establish strong Dhana Yoga potential. Long-term equity accumulation and structured asset diversification yield compounding security.`}
                    </p>
                  </>
                )}

                {/* PROPERTY & VEHICLES Deep-Dive */}
                {selectedDeepDiveTheme === 'property' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'भूमि, भवन, वाहन एवं चतुर्थांश (D4) विश्लेषण' : 'Real Estate, Vehicle Conveyance & D4 Chaturthamsha Analysis'}
                    </h4>
                    <p>
                      {isHi
                        ? `चतुर्थ भाव (${fourthHouse?.sign}, ${fourthSAV} बिंदु), भूमि कारक मंगल और वाहन कारक शुक्र का चतुर्थांश (D4) में शुभ प्रभाव अचल संपत्ति के निर्माण के लिए अनुकूल है। संपत्ति से जुड़े निर्णय कागजी जांच के उपरांत शुभ कालखंड में लेना श्रेयस्कर रहेगा।`
                        : `Real estate and physical asset acquisition (governed by 4th house in ${fourthHouse?.sign} with ${fourthSAV} bindus, Mars for land, Venus for vehicles, in D4 Chaturthamsha) indicate stable property ownership potential when executed during favorable dasha-transit alignments.`}
                    </p>
                  </>
                )}

                {/* HEALTH Deep-Dive */}
                {selectedDeepDiveTheme === 'health' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'शारीरिक जीवन शक्ति, त्रिदोष संतुलन एवं स्वास्थ्य प्रवृत्तियां' : 'Physical Vitality, Tridosha Balance & Constitutional Health'}
                    </h4>
                    <p>
                      {isHi
                        ? `लग्न भाव (${lagnaHouse?.sign}, ${lagnaSAV} बिंदु) और षष्ठ भाव के स्वामियों के आधार पर आपकी सामान्य जीवन शक्ति अच्छी है। चंद्रमा और बुध की स्थिति के कारण मानसिक तनाव और पाचन तंत्र पर विशेष ध्यान देना चाहिए। नियमित योग, प्राणायाम और सात्विक आहार स्वास्थ्य के लिए उत्तम संबल हैं।`
                        : `Constitutional vitality (anchored in Lagna with ${lagnaSAV} bindus and 6th house dynamics) indicates healthy recuperative strength. Moon-Mercury signatures suggest prioritizing gut health, stress modulation, and regular sleep cycles.`}
                    </p>
                  </>
                )}

                {/* SPIRITUALITY Deep-Dive */}
                {selectedDeepDiveTheme === 'spirituality' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'अध्यात्म, साधना, धर्म एवं विंशांश (D20) विश्लेषण' : 'Spiritual Evolution, Sadhana & D20 Vimsamsha Synthesis'}
                    </h4>
                    <p>
                      {isHi
                        ? `नवम भाव (${ninthHouse?.sign}), द्वादश भाव एवं विंशांश (D20) में गुरु-केतु के प्रभाव से आपकी रुचि जीवन के गूढ़ रहस्यों, ध्यान और निष्काम सेवा में है। नियमित साधना आपको आंतरिक शांति और स्पष्टता प्रदान करती है।`
                        : `Spiritual orientation (9th and 12th houses cross-validated in D20 Vimsamsha with Jupiter/Ketu) reflects an inclination toward meditative contemplation, ethical service, and Vedantic philosophy.`}
                    </p>
                  </>
                )}

                {/* TRAVEL Deep-Dive */}
                {selectedDeepDiveTheme === 'travel' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'देश-विदेश यात्रा, स्थान परिवर्तन एवं भाग्योदय' : 'Travel, Foreign Relocation & Horizon Expansion'}
                    </h4>
                    <p>
                      {isHi
                        ? `तृतीय, नवम और द्वादश भाव के स्वामी राहु-चंद्रमा के साथ मिलकर दूरस्थ यात्राओं और विदेश संपर्क से लाभ का मार्ग प्रशस्त करते हैं। अनुकूल गोचर में स्थान परिवर्तन भाग्योदय का कारक बन सकता है।`
                        : `Relocation indicators (3rd, 9th, and 12th house lords combined with Rahu and Moon) signify beneficial long-distance travels, cross-cultural engagements, and vocational opportunities connected to distant regions.`}
                    </p>
                  </>
                )}

                {/* BUSINESS Deep-Dive */}
                {selectedDeepDiveTheme === 'business' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'व्यापार, वाणिज्य, साझेदारी एवं दशमांश (D10) साक्ष्य' : 'Independent Business, Commerce & Enterprise Viability'}
                    </h4>
                    <p>
                      {isHi
                        ? `सप्तम (व्यापार साझीदार), दशम (कर्म) और एकादश (लाभ) के परस्पर संबंध स्वतंत्र उद्यम के लिए उपयुक्त हैं। बुध की शुभ स्थिति विपणन, परामर्श और तकनीकी वाणिज्य में सफलता का संकेत देती है।`
                        : `Commercial viability (7th, 10th, and 11th houses in D10 with Mercury) supports consulting, structured services, and tech-enabled commerce. Emphasize operational transparency and risk-hedged growth.`}
                    </p>
                  </>
                )}

                {/* PUBLIC SERVICE / POLITICS Deep-Dive */}
                {selectedDeepDiveTheme === 'politics' && (
                  <>
                    <h4 className="text-base font-bold font-serif text-astro-primary">
                      {isHi ? 'लोक सेवा, प्रशासनिक प्रभाव एवं सामाजिक नेतृत्व' : 'Public Service, Administrative Influence & Social Leadership'}
                    </h4>
                    <p>
                      {isHi
                        ? `पंचम, नवम और दशम भाव का सूर्य-मंगल के साथ संबंध सार्वजनिक जीवन और प्रशासनिक उत्तरदायित्वों में प्रभाव का संकेत देता है। देश-काल-पात्र के अनुसार सामाजिक निष्ठा और सेवा भाव से समाज में प्रतिष्ठा बढ़ती है।`
                        : `Leadership within public institutions (1st, 5th, 9th, and 10th houses governed by Sun and Mars) indicates natural administrative gravitas and community influence when grounded in public welfare and ethical integrity.`}
                    </p>
                  </>
                )}

              </div>
            </div>

          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 3: DYNAMIC FUTURE TIMING WINDOWS (Current Date Anchored)          */}
      {/* ========================================================================= */}
      {activeViewMode === 'timing-windows' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-8 shadow-md space-y-6">
            
            <div className="border-b border-astro-border pb-5">
              <div className="flex items-center gap-2 text-xs font-bold text-astro-accent uppercase tracking-wider mb-1">
                <Clock className="w-4 h-4" />
                <span>{isHi ? 'वर्तमान तिथि आधारित भविष्य काल निर्णय' : 'Current Date Anchored Timing Windows'}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary">
                {isHi ? `गतिशील काल निर्धारण एवं अवसर खिड़कियां (${currentMonthYearStr} स्थिति)` : `Dynamic Future Timing Windows (${currentMonthYearStr} Baseline)`}
              </h3>
              <p className="text-xs sm:text-sm text-astro-secondary-text mt-1">
                {isHi 
                  ? 'अतीत के समाप्त कालखंडों को छोड़कर, वर्तमान तिथि से आगामी विंशोत्तरी महादशा/अंतर्दशा एवं प्रमुख गोचर (गुरु, शनि, राहु) का संश्लेषित काल चक्र।' 
                  : `Eliminating all elapsed periods. Calculated strictly from current execution (${currentMonthYearStr}) evaluating active Mahadasha, Antardashas, and major planetary transits.`}
              </p>
            </div>

            {/* Timing Windows Matrix */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              
              {/* Window 1: Immediate Horizon */}
              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-3 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold bg-astro-primary text-white px-2.5 py-0.5 rounded-full">
                    {isHi ? 'खिड़की 1: निकट भविष्य' : 'Window 1: Near-Term'}
                  </span>
                  <span className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> High Favourability
                  </span>
                </div>
                <h4 className="font-serif font-bold text-sm text-astro-primary">
                  {isHi ? `वर्तमान - ${currentYear + 1} मध्य` : `Current - Mid ${currentYear + 1}`}
                </h4>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? `सक्रिय ${currentDasha?.planet} महादशा एवं ${activeAntardasha?.planet || 'सक्रिय'} अंतर्दशा का प्रभाव। कार्यक्षेत्र में स्थिरता, लंबित योजनाओं को पूर्ण करने और नए संपर्कों को स्थापित करने हेतु उत्तम काल।`
                    : `Energized by active ${currentDasha?.planet} Mahadasha and ${activeAntardasha?.planet || 'active'} Antardasha. Prime window for consolidating professional roles, executing delayed initiatives, and expanding strategic networks.`}
                </p>
                <div className="pt-2 border-t border-astro-border text-[11px] text-astro-primary font-semibold flex items-center justify-between">
                  <span>Gochar Support:</span>
                  <span className="text-emerald-800">Jupiter Trine / Solar Vitality</span>
                </div>
              </div>

              {/* Window 2: Medium Horizon */}
              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-3 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold bg-astro-primary text-white px-2.5 py-0.5 rounded-full">
                    {isHi ? 'खिड़की 2: मध्यकालिक' : 'Window 2: Mid-Term Horizon'}
                  </span>
                  <span className="text-xs font-bold text-astro-accent flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" /> Major Expansion
                  </span>
                </div>
                <h4 className="font-serif font-bold text-sm text-astro-primary">
                  {isHi ? `${currentYear + 1} उत्तरार्ध - ${currentYear + 2}` : `Late ${currentYear + 1} - ${currentYear + 2}`}
                </h4>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? `गुरु के राशि परिवर्तन एवं अंतर्दशा बदलाव के साथ आर्थिक संचय, पदोन्नति और पारिवारिक मांगलिक कार्यों के शुभ अवसर निर्मित होंगे।`
                    : `Jovian transit transition activates the 9th/11th houses, generating prime momentum for asset acquisitions, leadership promotions, and major familial milestones.`}
                </p>
                <div className="pt-2 border-t border-astro-border text-[11px] text-astro-primary font-semibold flex items-center justify-between">
                  <span>Dasha Shift:</span>
                  <span className="text-astro-primary">Harmonious Benefic Sub-Period</span>
                </div>
              </div>

              {/* Window 3: Long Horizon */}
              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-3 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold bg-astro-primary text-white px-2.5 py-0.5 rounded-full">
                    {isHi ? 'खिड़की 3: दीर्घकालिक' : 'Window 3: Long-Term Horizon'}
                  </span>
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" /> Authority & Mastery
                  </span>
                </div>
                <h4 className="font-serif font-bold text-sm text-astro-primary">
                  {isHi ? `${currentYear + 3} - ${currentYear + 5}` : `${currentYear + 3} - ${currentYear + 5}`}
                </h4>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? `शनि के दशम/एकादश भाव गोचर से दीर्घकालिक प्रयासों का स्थायी परिणाम प्राप्त होगा। सामाजिक प्रतिष्ठा और वित्तीय सुरक्षा का सुदृढ़ीकरण।`
                    : `Saturnian transit over upper houses rewards disciplined long-term consistency, solidifying lasting social stature, institutional standing, and generational security.`}
                </p>
                <div className="pt-2 border-t border-astro-border text-[11px] text-astro-primary font-semibold flex items-center justify-between">
                  <span>Cumulative Impact:</span>
                  <span className="text-astro-primary">High Stability & Fruitfulness</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 4: REMEDIAL WISDOM & HARMONIZATION (Zero Fear-Mongering)          */}
      {/* ========================================================================= */}
      {activeViewMode === 'remedial-harmony' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-8 shadow-md space-y-6">
            
            <div className="border-b border-astro-border pb-5">
              <div className="flex items-center gap-2 text-xs font-bold text-astro-accent uppercase tracking-wider mb-1">
                <Shield className="w-4 h-4" />
                <span>{isHi ? 'शास्त्रीय विवेकपूर्ण संतुलन एवं उपाय' : 'Proportionate Remedial Wisdom'}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-primary">
                {isHi ? 'शास्त्रीय ऊर्जा संतुलन, दैनिक साधना एवं विवेकपूर्ण मार्गदर्शन' : 'Constructive Energetic Harmonization & Daily Sadhana'}
              </h3>
              <p className="text-xs sm:text-sm text-astro-secondary-text mt-1">
                {isHi 
                  ? 'भय-मुक्त, यथार्थवादी एवं व्यावहारिक उपाय — रत्न केवल आवश्यकता पड़ने पर, सात्विक जीवनशैली एवं अनुशासन को सर्वोपरि महत्व।' 
                  : 'Constructive, ethical, and fear-free remedial philosophy. Prioritizing disciplined lifestyle, meditation, and satvik charity over superstitious rituals.'}
              </p>
            </div>

            {/* Official Astrological Verdict */}
            <div className="bg-emerald-50/80 border border-emerald-300 p-6 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 text-emerald-900 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                <span>{isHi ? 'शास्त्रीय उपाय निर्णय (Astrological Verdict):' : 'Astrological Remedial Assessment:'}</span>
              </div>
              <p className="text-xs sm:text-sm text-emerald-950 leading-relaxed">
                {isHi
                  ? 'उपाय: आपकी कुंडली में कोई विनाशकारी ग्रह स्थिति नहीं है। इस समय किसी भारी अथवा भय-आधारित अनुष्ठान की आवश्यकता नहीं है। दैनिक अनुशासन, सत्यनिष्ठा, माता-पिता का सम्मान और नियमित एकाग्रता ही आपका सर्वश्रेष्ठ संबल है।'
                  : 'Verdict: None specifically required at this time. Your primary natal pillars remain structurally sound. Natural satvik habits, ethical business practices, and regular mental centering provide optimal planetary balance.'}
              </p>
            </div>

            {/* Constructive Remedial Practices */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              
              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold">
                  <Sun className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'दैनिक साधना व मानसिक शांति' : 'Daily Mindfulness & Prana'}</span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'प्रतिदिन सूर्योदय के समय 10 मिनट गायत्री मंत्र अथवा ॐ का जप और प्राणायाम करें। इससे मन में स्पष्टता और निर्णय शक्ति मजबूत होती है।'
                    : '10 minutes of morning breathwork (Pranayama) or Gayatri mantra recitation stabilizes mental fortitude and clears cognitive fatigue.'}
                </p>
              </div>

              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold">
                  <Heart className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'सात्विक दान व सेवा' : 'Satvik Charity & Service'}</span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'शनिवार अथवा बुधवार को जरूरतमंद विद्यार्थियों को पुस्तकें अथवा भोजन का दान करें। निस्वार्थ सेवा से राहु और शनि के प्रतिकूल प्रभाव शांत होते हैं।'
                    : 'Providing educational support or meals to underprivileged students on Wednesdays/Saturdays harmonizes Mercury and Saturnian vibrations.'}
                </p>
              </div>

              <div className="bg-[#FAF6ED] p-5 rounded-2xl border border-astro-border space-y-2">
                <div className="flex items-center gap-2 text-astro-primary font-bold">
                  <Shield className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'रत्न व धारण नीति' : 'Gemstone Policy'}</span>
                </div>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'रत्न कभी भी बिना गहन व्यक्तिगत परामर्श के धारण न करें। लग्न स्वामी की मजबूती हेतु प्राकृतिक पंचधातु कड़ा अथवा रुद्राक्ष अधिक सुरक्षित और प्रभावी रहता है।'
                    : 'Gemstones are never automatically prescribed. Natural Rudraksha or balancing metals are safer alternatives for strengthening Lagna vitality.'}
                </p>
              </div>

            </div>

          </div>
        </div>
      )}

    </div>
  );
};
